
import { optsValidator } from '../../_utils/useValidationUtils'

const MAX_COLUMNS = 12

const GAPS = [
    '0',
    'px',
    '0_5',
    '1',
    '1_5',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
]

const COLS = Array.from({ length: MAX_COLUMNS }, (_, index) =>
    String(index + 1)
)

const parseGapValues = (value) => {
    const values = value.trim().split(/\s+/)

    return {
        values,
        rowGap: values[0],
        columnGap: values[1] || values[0],
    }
}

const normaliseVNodeClasses = (value) => {
    const classes = []

    const appendClasses = (classValue) => {
        if (!classValue) {
            return
        }

        if (Array.isArray(classValue)) {
            classValue.forEach(appendClasses)
            return
        }

        if (typeof classValue === 'string') {
            classes.push(...classValue.split(/\s+/).filter(Boolean))
            return
        }

        if (typeof classValue === 'object') {
            Object.keys(classValue).forEach((className) => {
                if (classValue[className]) {
                    classes.push(className)
                }
            })
        }
    }

    appendClasses(value)

    return classes
}

/**
 * A responsive masonry layout with left-to-right source order and support for
 * the existing col-span-1 through col-span-12 and col-span-full classes.
 *
 * @displayName Masonry
 */
export default {
    name: 'UiMasonry',
    inheritAttrs: false,
    props: {
        /**
         * HTML tag: div, section, ul etc
         * @default div
         */
        tag: {
            type: String,
            default: 'div',
        },
        /**
         * Gap size: one value or row and column values separated by whitespace
         * @default 4
         * @values 0, px, 0_5, 1, 1_5, 2, 3, 4, 5, 6, 7, 8, 9, 10
         */
        gap: {
            type: String,
            default: '4',
            validator: (val) => {
                const { values } = parseGapValues(val)

                return (
                    values.length <= 2 &&
                    values.every((value) => optsValidator(value, GAPS))
                )
            },
        },
        /**
         * Number of masonry columns when minColWidth is not set
         * @default 12
         * @values 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12
         */
        cols: {
            type: String,
            default: '12',
            validator: (val) => optsValidator(val, COLS),
        },
        /**
         * Minimum rendered width of an item before items wrap. Cols defaults to 12.
         */
        minColWidth: {
            type: Number,
        },
        /**
         * Stretch items downward until they reach the next item below
         * @default false
         */
        stretch: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            containerHeight: 0,
            ready: false,
        }
    },
    computed: {
        classes() {
            const classes = ['masonry']
            const { rowGap, columnGap } = parseGapValues(this.gap)

            if (rowGap !== '0') {
                classes.push(`gap-row-${rowGap}`)
            }

            if (columnGap !== '0') {
                classes.push(`gap-col-${columnGap}`)
            }

            if (this.ready) {
                classes.push('masonry--ready')
            }

            return classes
        },
        containerStyles() {
            return {
                height: `${this.containerHeight}px`,
            }
        },
    },
    watch: {
        cols: 'scheduleLayout',
        gap: 'scheduleLayout',
        minColWidth: 'scheduleLayout',
    },
    created() {
        this.resizeObserver = null
        this.resizeFrame = null
        this.observedItems = new Set()
        this.observedContainerWidth = null
    },
    mounted() {
        const container = this.$refs.container

        container.addEventListener('load', this.scheduleLayout, true)

        this.setupResizeObserver()
        this.syncObservedItems()
        this.scheduleLayout()
    },
    updated() {
        this.syncObservedItems()
        this.scheduleLayout()
    },
    beforeDestroy() {
        this.removeLoadListener()
        this.teardownResizeObserver()
        this.cancelScheduledLayout()
    },
    methods: {
        setupResizeObserver() {
            if (!window.ResizeObserver) {
                window.addEventListener('resize', this.scheduleLayout)
                return
            }

            const container = this.$refs.container

            this.resizeObserver = new ResizeObserver((entries) => {
                const shouldLayout = entries.some((entry) => {
                    if (entry.target !== container) {
                        return true
                    }

                    const width = entry.contentRect.width

                    if (width === this.observedContainerWidth) {
                        return false
                    }

                    this.observedContainerWidth = width

                    return true
                })

                if (shouldLayout) {
                    this.scheduleLayout()
                }
            })

            this.resizeObserver.observe(container)
        },
        removeLoadListener() {
            const container = this.$refs.container

            if (container) {
                container.removeEventListener('load', this.scheduleLayout, true)
            }
        },
        teardownResizeObserver() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect()
                return
            }

            window.removeEventListener('resize', this.scheduleLayout)
        },
        cancelScheduledLayout() {
            if (this.resizeFrame) {
                window.cancelAnimationFrame(this.resizeFrame)
                this.resizeFrame = null
            }
        },
        syncObservedItems() {
            if (!this.resizeObserver) {
                return
            }

            const currentItems = new Set(this.getItems())

            this.observedItems.forEach((item) => {
                if (!currentItems.has(item)) {
                    this.resizeObserver.unobserve(item)
                    this.observedItems.delete(item)
                }
            })

            currentItems.forEach((item) => {
                if (!this.observedItems.has(item)) {
                    this.resizeObserver.observe(item)
                    this.observedItems.add(item)
                }
            })
        },
        getItems() {
            const container = this.$refs.container

            return container ? Array.from(container.children) : []
        },
        scheduleLayout() {
            if (this.resizeFrame) {
                return
            }

            this.$nextTick(() => {
                if (this.resizeFrame) {
                    return
                }

                this.resizeFrame = window.requestAnimationFrame(() => {
                    this.resizeFrame = null
                    this.layoutItems()
                })
            })
        },
        layoutItems() {
            const container = this.$refs.container
            const items = this.getItems()

            if (!container || !container.clientWidth) {
                return
            }

            if (!items.length) {
                this.markLayoutReady(0)
                return
            }

            const computedStyles = window.getComputedStyle(container)
            const rowGap = this.parseGap(computedStyles.rowGap)
            const columnGap = this.parseGap(computedStyles.columnGap)
            const metrics = this.getLayoutMetrics(container, columnGap)
            const itemSpans = this.getEffectiveItemSpans(
                items,
                metrics.containerWidth,
                columnGap,
                metrics.columnCount
            )

            this.applyItemWidths(
                items,
                itemSpans,
                metrics.columnWidth,
                columnGap
            )

            const itemHeights = items.map((item) =>
                this.getNaturalItemHeight(item)
            )
            const { placements, containerHeight } = this.placeItems(
                items,
                itemSpans,
                itemHeights,
                metrics,
                rowGap
            )

            this.applyStretching(
                items,
                placements,
                itemHeights,
                containerHeight,
                rowGap
            )
            this.markLayoutReady(containerHeight)
        },
        getLayoutMetrics(container, columnGap) {
            const containerWidth = container.clientWidth
            const columnCount = this.minColWidth
                ? MAX_COLUMNS
                : Number(this.cols)
            const availableWidth =
                containerWidth - columnGap * (columnCount - 1)
            const columnWidth = availableWidth / columnCount

            return {
                containerWidth,
                columnCount,
                columnWidth,
            }
        },
        applyItemWidths(items, itemSpans, columnWidth, columnGap) {
            items.forEach((item, index) => {
                const span = itemSpans[index]
                const itemWidth = columnWidth * span + columnGap * (span - 1)

                item.style.width = `${itemWidth}px`
                item.style.height = ''
            })
        },
        placeItems(items, itemSpans, itemHeights, metrics, rowGap) {
            const columnHeights = Array(metrics.columnCount).fill(0)
            const placements = []

            items.forEach((item, index) => {
                const span = itemSpans[index]
                const placement = this.getItemPlacement(columnHeights, span)
                const nextHeight = placement.top + itemHeights[index] + rowGap

                placements[index] = {
                    column: placement.column,
                    top: placement.top,
                    span,
                    height: itemHeights[index],
                }

                item.style.left = `${
                    placement.column *
                    (metrics.columnWidth +
                        rowGap -
                        rowGap +
                        this.parseGap(
                            `${metrics.columnWidth + rowGap - metrics.columnWidth}px`
                        ))
                }px`
                item.style.top = `${placement.top}px`

                for (
                    let columnIndex = placement.column;
                    columnIndex < placement.column + span;
                    columnIndex += 1
                ) {
                    columnHeights[columnIndex] = nextHeight
                }
            })

            return {
                placements,
                containerHeight: Math.max(...columnHeights) - rowGap,
            }
        },
        applyStretching(
            items,
            placements,
            itemHeights,
            containerHeight,
            rowGap
        ) {
            const stretchedHeights = this.stretch
                ? this.getStretchedItemHeights(
                      placements,
                      itemHeights,
                      containerHeight,
                      rowGap
                  )
                : itemHeights

            items.forEach((item, index) => {
                item.style.height = this.stretch
                    ? `${stretchedHeights[index]}px`
                    : ''
            })
        },
        markLayoutReady(containerHeight) {
            this.containerHeight = containerHeight
            this.ready = true
        },
        getNaturalItemHeight(item) {
            const child = item.firstElementChild

            if (!child) {
                return item.getBoundingClientRect().height
            }

            const previousItemHeight = item.style.height
            const previousChildHeight = child.style.height
            const previousChildMinHeight = child.style.minHeight

            item.style.height = ''
            child.style.height = 'auto'
            child.style.minHeight = '0'

            const height = Math.max(
                child.getBoundingClientRect().height,
                child.scrollHeight
            )

            item.style.height = previousItemHeight
            child.style.height = previousChildHeight
            child.style.minHeight = previousChildMinHeight

            return height
        },
        getStretchedItemHeights(
            placements,
            itemHeights,
            containerHeight,
            rowGap
        ) {
            return placements.map((placement, index) => {
                let nextTop = containerHeight
                let hasItemBelow = false

                placements.forEach((candidate, candidateIndex) => {
                    if (candidateIndex === index) {
                        return
                    }

                    if (candidate.top <= placement.top) {
                        return
                    }

                    if (!this.itemsOverlapHorizontally(placement, candidate)) {
                        return
                    }

                    if (candidate.top < nextTop) {
                        nextTop = candidate.top
                        hasItemBelow = true
                    }
                })

                const availableHeight = hasItemBelow
                    ? nextTop - placement.top - rowGap
                    : nextTop - placement.top

                return Math.max(itemHeights[index], availableHeight)
            })
        },
        itemsOverlapHorizontally(item, candidate) {
            return (
                candidate.column < item.column + item.span &&
                candidate.column + candidate.span > item.column
            )
        },
        getEffectiveItemSpans(items, containerWidth, columnGap, columnCount) {
            const requestedSpans = items.map((item) =>
                this.getRequestedItemSpan(item, columnCount)
            )

            if (!this.minColWidth) {
                return requestedSpans
            }

            const availableWidth =
                containerWidth - columnGap * (columnCount - 1)
            const columnWidth = availableWidth / columnCount
            const minimumSpan = this.getMinimumItemSpan(
                columnWidth,
                columnGap,
                columnCount
            )
            const effectiveSpans = Array(items.length).fill(1)
            let row = []
            let usedColumns = 0

            const completeRow = () => {
                if (!row.length) {
                    return
                }

                this.distributeRemainingColumns(
                    row,
                    effectiveSpans,
                    columnCount - usedColumns
                )

                row = []
                usedColumns = 0
            }

            requestedSpans.forEach((requestedSpan, index) => {
                const span = Math.min(
                    Math.max(requestedSpan, minimumSpan),
                    columnCount
                )

                if (row.length && usedColumns + span > columnCount) {
                    completeRow()
                }

                row.push({ index })
                effectiveSpans[index] = span
                usedColumns += span

                if (usedColumns === columnCount) {
                    completeRow()
                }
            })

            completeRow()

            return effectiveSpans
        },
        distributeRemainingColumns(row, effectiveSpans, remainingColumns) {
            let rowIndex = 0

            while (remainingColumns > 0) {
                const item = row[rowIndex % row.length]

                effectiveSpans[item.index] += 1
                remainingColumns -= 1
                rowIndex += 1
            }
        },
        getRequestedItemSpan(item, columnCount) {
            const value = item.dataset.colSpan

            if (value === 'full') {
                return columnCount
            }

            const requestedSpan = Number(value) || 1

            return Math.min(Math.max(requestedSpan, 1), columnCount)
        },
        getMinimumItemSpan(columnWidth, columnGap, columnCount) {
            if (columnWidth <= 0) {
                return columnCount
            }

            const minimumSpan = Math.ceil(
                (this.minColWidth + columnGap) / (columnWidth + columnGap)
            )

            return Math.min(Math.max(minimumSpan, 1), columnCount)
        },
        getItemPlacement(columnHeights, span) {
            let bestColumn = 0
            let bestTop = Infinity
            const lastStartColumn = columnHeights.length - span

            for (
                let startColumn = 0;
                startColumn <= lastStartColumn;
                startColumn += 1
            ) {
                const top = Math.max(
                    ...columnHeights.slice(startColumn, startColumn + span)
                )

                if (top < bestTop) {
                    bestColumn = startColumn
                    bestTop = top
                }
            }

            return {
                column: bestColumn,
                top: bestTop,
            }
        },
        getVNodeSpan(vnode) {
            const data = vnode.data || {}
            const classNames = [
                ...normaliseVNodeClasses(data.staticClass),
                ...normaliseVNodeClasses(data.class),
                ...normaliseVNodeClasses(data.attrs && data.attrs.class),
            ]

            if (classNames.includes('col-span-full')) {
                return 'full'
            }

            const spanClasses = classNames.filter((className) =>
                /^col-span-\d+$/.test(className)
            )
            const spanClass = spanClasses[spanClasses.length - 1]

            return spanClass ? spanClass.replace('col-span-', '') : '1'
        },
        parseGap(value) {
            const parsedValue = Number.parseFloat(value)

            return Number.isFinite(parsedValue) ? parsedValue : 0
        },
    },
    render(h) {
        const slotItems = (this.$slots.default || []).filter(
            (vnode) => vnode && vnode.tag
        )

        return h(
            this.tag,
            {
                ref: 'container',
                class: this.classes,
                style: this.containerStyles,
                attrs: this.$attrs,
            },
            slotItems.map((vnode, index) => {
                const span = this.getVNodeSpan(vnode)

                return h(
                    'div',
                    {
                        key:
                            vnode.key !== undefined && vnode.key !== null
                                ? vnode.key
                                : `masonry-item-${index}`,
                        class: 'masonry__item',
                        attrs: {
                            'data-col-span': span,
                        },
                    },
                    [vnode]
                )
            })
        )
    },
}
